import type { AcceptedPlayReviewRequest } from '@/containers/agents/use-accepted-play-review.hook';
import {
  QuestionCardInputContext,
  type QuestionCardInput,
} from './question-card-input-context';
import { useAuth } from '@clerk/react';
import type { ContextMeter } from '@/components/agent-chat/context-meter.util';
import type { TurnProgress } from '@/components/agent-chat/play-draft.util';
import { CopilotToolUIs } from '@/components/agent-chat/copilot-tool-uis';
import { MessageQueueContext } from '@/components/agent-chat/message-queue-context';
import { getConversationLabel } from '@/components/copilot/conversation-label.util';
import { useAgentChatRuntime } from '@/hooks/use-agent-chat-runtime.hook';
import {
  useAgentChatHosts,
  type AgentChatAttachment,
} from '@/containers/agents/use-agent-chat-hosts.hook';
import { useChatTitles, withChatTitles } from '@/hooks/chat-titles.store';
import type { LocalChats } from '@/hooks/local-chats.util';
import type {
  AgentContext,
  AgentMessage,
  ChatSnapshot,
  ConversationSummary,
  SendOptions,
  SendResult,
} from '@/hooks/use-agent-chat.hook';
import { announceFreshChat } from '@/components/agent-chat/fresh-chat.util';
import { cluModelPickOf } from '@/components/agent-chat/model-picker/clu-models';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import { useAppDispatch, useAppSelector } from '@/store';
import { useGetPlansQuery } from '@/store/features/billing.feature';
import { useGetLeadListsQuery } from '@/store/features/lead-lists.feature';
import { isExtensionLeads } from '@/utils/extension-leads.utils';
import {
  selectAiChatContext,
  setAiChatScope,
  selectAiChatInitialMessage,
  selectAiChatInitialModelPick,
  selectAiChatOpen,
  selectAiChatOpenConversationId,
  selectAiChatPendingSend,
  selectAiChatSessions,
  sendAiChatMessage,
  openAiChatConversation,
  setAiChatContext,
  setAiChatInitialMessage,
  setAiChatSessions,
  setAiChatSessionState,
} from '@/store/features/ui.feature';
import type { LeadList } from '@/types';
import type {
  Unstable_DirectiveFormatter,
  Unstable_MentionAdapter,
  Unstable_MentionItem,
} from '@assistant-ui/core';
import {
  AssistantRuntimeProvider,
  useExternalStoreRuntime,
  type ExternalStoreAdapter,
} from '@assistant-ui/react';
import { useParams, useRouterState } from '@tanstack/react-router';
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** The backend allows 30 chat requests/min per user; cap concurrent sessions. */
export const MAX_COPILOT_SESSIONS = 5;
/** The first session. It lives in the provider itself and keeps the
 *  aiChatState / aiChatConversationId persistence keys. */
const PRIMARY_SESSION_KEY = 'primary';
const SINGLE_SESSION = {
  keys: [PRIMARY_SESSION_KEY],
  activeKey: PRIMARY_SESSION_KEY,
};
const GENERAL_CONTEXT: AgentContext = { type: 'general' };
const NO_MESSAGES: AgentMessage[] = [];
const IDLE_ADAPTER: ExternalStoreAdapter = {
  messages: [],
  onNew: async () => {},
};

/**
 * running: streaming. needs_input: waiting on a confirmation card.
 * unread: a response finished while the session was in the background and it
 * hasn't been viewed since. done: viewed, has a response. idle: empty.
 */
export type CopilotSessionStatus =
  | 'running'
  | 'needs_input'
  | 'unread'
  | 'done'
  | 'idle';

export type CopilotSession = {
  key: string;
  kind: 'clu';
  title: string;
  conversationId: string | null;
  status: CopilotSessionStatus;
  error: boolean;
};

type RuntimeApi = ReturnType<typeof useAgentChatRuntime>;

/** The slice of a session's runtime the provider renders from. */
type SessionApi = Pick<
  RuntimeApi,
  | 'runtime'
  | 'acceptedPlayReviews'
  | 'retryAcceptedPlayReview'
  | 'messages'
  | 'isStreaming'
  | 'conversationId'
  | 'awaitingConfirmation'
  | 'pendingConfirmation'
  | 'contextMeter'
  | 'progress'
  | 'modelPick'
  | 'setModelPick'
  | 'conversations'
  | 'historyFailed'
  | 'historyScope'
  | 'isLoadingHistory'
  | 'localChats'
  | 'settleAgentChats'
  | 'isLoadingConversation'
  | 'error'
  | 'errorCode'
  | 'sendMessage'
  | 'failedTextTurn'
  | 'retryFailedTextTurn'
  | 'confirmAction'
  | 'rejectAction'
  | 'clearChat'
  | 'fetchConversations'
  | 'loadConversation'
  | 'deleteConversation'
  | 'snapshotChat'
  | 'replaceChat'
  | 'messageQueue'
  | 'listMentionAdapter'
  | 'listMentionFormatter'
  | 'listMentionItems'
> & { key: string };

type SessionReport = Pick<
  CopilotSession,
  'title' | 'conversationId' | 'error'
> & {
  running: boolean;
  needsInput: boolean;
  hasResponse: boolean;
  isActive: boolean;
};

const isBlankSession = (session: SessionApi | null, activeKey: string) =>
  session?.key === activeKey &&
  session.messages.length === 0 &&
  !session.conversationId &&
  !session.isStreaming;

/** A chat that runs, or waits on the user's answer to a card. */
const isBusy = (status: CopilotSessionStatus | undefined) =>
  status === 'running' || status === 'needs_input';

/**
 * At the cap, the session a new chat takes while the one on screen is busy,
 * so a running chat keeps its stream (CLE-567): the oldest one doing
 * nothing, one with an unread reply last. None while every one is busy.
 */
function spareSession(
  keys: readonly string[],
  activeKey: string,
  summaries: Record<string, CopilotSession>
): string | undefined {
  const idle = keys.filter(
    (key) => key !== activeKey && !isBusy(summaries[key]?.status)
  );
  return idle.find((key) => summaries[key]?.status !== 'unread') ?? idle[0];
}

/** The same set while its ids stay the same, so a reader re-renders only
 *  when a chat starts or stops running. */
function useStableIds(ids: Iterable<string>): ReadonlySet<string> {
  const key = [...new Set(ids)].sort().join('\n');
  return useMemo(() => new Set(key ? key.split('\n') : []), [key]);
}

/**
 * Everything a Copilot *presentation* (docked sidebar or full-screen
 * `/clu`) needs to render its Thread + header, EXCEPT the runtime itself
 * (the runtime is shared via `AssistantRuntimeProvider`, read by `<Thread />`).
 *
 * Confirmation handling (`onConfirm` / `onReject` / `pendingConfirmationId`)
 * is NOT exposed here - it is consumed only by `CopilotToolUIs`, which this
 * provider mounts once at the top of the tree, so neither presentation needs
 * to thread it through.
 */
type CopilotRuntimeContextValue = {
  /** Agent pages lease a persistent host; releasing a view never stops it. */
  attachAgentChat: (input: AgentChatAttachment) => () => void;
  enqueueAcceptedPlayReview: (request: AcceptedPlayReviewRequest) => boolean;
  acceptedPlayReviews: RuntimeApi['acceptedPlayReviews'];
  savedPlayReviewRequests: readonly AcceptedPlayReviewRequest[];
  interruptedPlayReviews: readonly {
    organizationId: string;
    agentId: string;
    playId: string;
  }[];
  markPlayReviewInterrupted: (
    input: { organizationId: string; agentId: string; playId: string },
    interrupted: boolean
  ) => void;
  retryAcceptedPlayReview: RuntimeApi['retryAcceptedPlayReview'];
  agentSession: {
    agentId: string;
    requestedChatId: string | undefined;
    ready: boolean;
  } | null;
  messages: AgentMessage[];
  isStreaming: boolean;
  conversationId: string | null;
  /**
   * Every chat with a turn running in this tab, by id: the one on screen,
   * Clu sessions in the background and agents' chats (CLE-567).
   */
  runningChatIds: ReadonlySet<string>;
  awaitingConfirmation: boolean;
  conversations: ConversationSummary[];
  historyFailed?: boolean;
  historyScope?: string;
  isLoadingHistory?: boolean;
  /** Chats the lists don't show yet: one just started, recent sends. */
  localChats: LocalChats;
  /** A read of the agents' chats came back: see useAgentChat. */
  settleAgentChats: (
    listed: ReadonlySet<string>,
    agentIds: ReadonlySet<string>
  ) => void;
  clearChat: () => void;
  fetchConversations: () => Promise<void> | void;
  /** Deletes a Clu or agent chat; true when the server did. */
  deleteConversation: (conversationId: string) => Promise<boolean>;
  // `sendMessage` resolves with a SendResult (ok/aborted) since the confirm
  // retry work; presentations that fire-and-forget can keep ignoring it.
  sendMessage: (
    message: string,
    options?: SendOptions
  ) => Promise<SendResult> | void;
  failedTextTurn: RuntimeApi['failedTextTurn'];
  retryFailedTextTurn: RuntimeApi['retryFailedTextTurn'];
  /** True while a conversation from history is being opened. */
  isLoadingConversation: boolean;
  /** Opens a conversation from history into the active session. */
  loadConversation: (conversationId: string) => Promise<void>;
  /** The selected chat as it stands, and restoring a saved snapshot. */
  snapshotChat: () => ChatSnapshot;
  replaceChat: (snapshot: ChatSnapshot) => void;
  /** Why the last turn (or opening a conversation) failed; null otherwise. */
  error: string | null;
  /** The server's code for that failure, while it shows (credits_held). */
  errorCode: string | null;
  /** How full the prompt of the chat's latest turn was; null before one. */
  contextMeter: ContextMeter | null;
  /** The play Clu is writing in the running turn ("step 3 of 5"). */
  progress: TurnProgress | null;
  /** clu_model_v1 router: the chat's model pick, and changing it. */
  modelPick: RuntimeApi['modelPick'];
  setModelPick: RuntimeApi['setModelPick'];
  listMentionAdapter: Unstable_MentionAdapter;
  listMentionFormatter: Unstable_DirectiveFormatter;
  listMentionItems: Unstable_MentionItem[];
  lists: LeadList[] | undefined;
  // --- Session manager. Every field above refers to the ACTIVE session.
  sessions: CopilotSession[];
  activeSessionKey: string;
  canOpenSession: boolean;
  /** Focus a blank session (reusing the active one if it is blank). False
   *  when none could be opened (at the cap): callers then fall back to
   *  clearChat(). */
  newSession: () => boolean;
  /** New chat: a blank session, else today's clear; then the chat greets. */
  startNewChat: () => void;
  switchSession: (key: string) => void;
  /** Aborts only that session's stream and drops its persisted state. */
  closeSession: (key: string) => void;
  /** "Archive today's chats": close every session that is not running or
   *  waiting on input. History stays on the server. */
  clearSessions: () => void;
  /** Focus the session already showing this conversation, else open it in a
   *  new one. Blank active session / at the cap: load it into the active
   *  session in place. */
  openConversation: (conversationId: string) => void;
};

const CopilotRuntimeContext = createContext<CopilotRuntimeContextValue | null>(
  null
);

export const useCopilotRuntime = () => {
  const ctx = useContext(CopilotRuntimeContext);
  if (!ctx) {
    throw new Error(
      'useCopilotRuntime must be used inside <CopilotRuntimeProvider>.'
    );
  }
  return ctx;
};

/** The chat's failure, or null; null outside a provider too. */
export const useCopilotRuntimeError = (): string | null =>
  useContext(CopilotRuntimeContext)?.error ?? null;

/** The shared chat, or null outside a provider (a page rendered on its own). */
export const useCopilotRuntimeOptional =
  (): CopilotRuntimeContextValue | null => useContext(CopilotRuntimeContext);

/**
 * True when the active route is the full-screen Copilot. Drives `enabled`
 * alongside the docked-sidebar open flag so the runtime is live on `/clu`
 * even when the sidebar is closed.
 */
export const useIsCopilotRoute = () =>
  useRouterState({ select: (s) => s.location.pathname === '/clu' });

/**
 * True when the active route is the home page. Drives `enabled` so the runtime
 * is live on `/` for the Copilot-on-Home surface — but only for a resolved
 * non-AppSumo org (see the `enabled` gate below), since AppSumo-only orgs get
 * the legacy prospecting home at `/`, not the Copilot.
 */
export const useIsHomeRoute = () =>
  useRouterState({ select: (s) => s.location.pathname === '/' });

/**
 * Owns persistent chat runtimes and shares the selected one across presentations.
 *
 * Why hoisted (was local to `AiSidebar`): "one conversation, two
 * presentations." The docked sidebar and `/clu` must read the SAME thread
 * and the SAME SSE stream. The runtime + `AssistantRuntimeProvider` therefore
 * live here, above both surfaces, and never unmount on a presentation switch -
 * so an in-flight stream survives `/clu` -> docked sidebar.
 *
 * Sessions: the primary session's runtime stays here; each extra session gets
 * a `CopilotSessionHost` with its own runtime + stream. Both presentations
 * always show the ACTIVE session.
 *
 * `enabled = sidebarOpen || onCopilotRoute || (onHomeRoute && non-AppSumo)`.
 * When none is true the runtime idles (lists queries skip); the hook
 * still runs (hooks can't be conditional) but does no network work. The home
 * term is gated on a RESOLVED non-AppSumo access decision (`isReady &&
 * !isAppSumoOnly`) so the runtime never warms for AppSumo-only orgs (who get
 * the legacy prospecting home at `/`, not the Copilot) and never acts on a
 * stale seed before access is known. Fail-closed: while access loads the home
 * term is false.
 */
export function CopilotRuntimeProvider({ children }: { children: ReactNode }) {
  const { orgId, userId } = useAuth();
  const scope = userId ? JSON.stringify([orgId ?? null, userId]) : null;
  const storedScope = useAppSelector((state) => state.ui.aiChatScope);
  const dispatch = useAppDispatch();
  // Retain the last known owner during Clerk loading. Before a different
  // owner mounts, remove only transient chat state, including queued prompts.
  useLayoutEffect(() => {
    if (scope && scope !== storedScope) dispatch(setAiChatScope(scope));
  }, [scope, storedScope, dispatch]);
  if (!scope || storedScope !== scope) return null;
  return (
    <ScopedCopilotRuntimeProvider key={scope}>
      {children}
    </ScopedCopilotRuntimeProvider>
  );
}

function ScopedCopilotRuntimeProvider({ children }: { children: ReactNode }) {
  const { orgId } = useAuth();
  const dispatch = useAppDispatch();
  const sidebarOpen = useAppSelector(selectAiChatOpen);
  const onCopilotRoute = useIsCopilotRoute();
  const onHomeRoute = useIsHomeRoute();
  // Mirror the CopilotSurfaceGate fence: keep the runtime idle for AppSumo-only
  // orgs and while access is still resolving. isAppSumoOnly derives from the
  // plans query (loads independently of isReady), so gate on plansLoading too —
  // otherwise the runtime would warm during the fail-open window.
  const { isAppSumoOnly, isReady, organizationId } = useFeatureAccess();
  // plansError too: on a plans-query error isAppSumoOnly is falsely false, so
  // keep the runtime idle on home (fail-closed) rather than warm it for a
  // possibly-AppSumo org.
  const { isLoading: plansLoading, isError: plansError } = useGetPlansQuery(
    { organizationId },
    { skip: !organizationId }
  );
  const enabled =
    Boolean(orgId) &&
    (sidebarOpen ||
      onCopilotRoute ||
      (onHomeRoute &&
        isReady &&
        !plansLoading &&
        !plansError &&
        !isAppSumoOnly));

  const aiChatContext = useAppSelector(selectAiChatContext);
  const { listId } = useParams({ strict: false }) as { listId?: string };

  // Skip must track `enabled`, not just sidebar-open: on `/clu` (sidebar
  // closed) we still need lists for the list-context effect + `onSelectList`,
  // otherwise both silently no-op.
  const { data: allLists } = useGetLeadListsQuery(undefined, {
    skip: !enabled,
  });

  // Scope the chat to the active route's list (was AiSidebar 115-133).
  useEffect(() => {
    if (!enabled || !listId || !allLists) return;
    if (
      aiChatContext?.type === 'lead_list' &&
      aiChatContext.lead_list_id === listId
    ) {
      return;
    }
    const currentList = allLists.find((list) => list.id === listId);
    dispatch(
      setAiChatContext({
        type: 'lead_list',
        lead_list_id: listId,
        list_name: currentList?.name,
        lead_count: currentList?.prospect_count,
      })
    );
  }, [aiChatContext, allLists, dispatch, enabled, listId]);

  // General sessions keep their context when an Agent workspace opens. Each
  // Agent host below owns its fixed context instead of borrowing this one.
  const generalContextRef = useRef<AgentContext>(GENERAL_CONTEXT);
  if (aiChatContext?.type !== 'agent')
    generalContextRef.current = aiChatContext ?? GENERAL_CONTEXT;
  const context = generalContextRef.current;
  const { unlocked: agentsEnabled } = useAgentsAccess();
  const agentHosts = useAgentChatHosts(agentsEnabled, orgId ?? undefined);
  const agentAttachment = agentHosts.attachment;
  const {
    reportConversation,
    newChat: newAgentChat,
    openConversation: openAgentConversation,
    forget: forgetAgentHosts,
  } = agentHosts;

  const onSelectList = useCallback(
    (list: LeadList | null) => {
      if (list) {
        dispatch(
          setAiChatContext({
            type: 'lead_list',
            lead_list_id: list.id,
            list_name: list.name,
            lead_count: list.prospect_count,
          })
        );
      } else {
        dispatch(setAiChatContext({ type: 'general' }));
      }
    },
    [dispatch]
  );

  // --- Session manager. Before any session is stored: the primary alone.
  const storedSessions = useAppSelector(selectAiChatSessions);
  const { keys, activeKey } = storedSessions || SINGLE_SESSION;
  const [summaries, setSummaries] = useState<Record<string, CopilotSession>>(
    {}
  );
  const [reported, setReported] = useState<SessionApi | null>(null);
  const closersRef = useRef(new Map<string, () => void>());
  // Latest values for the stable actions below: session hosts are memoized,
  // so the actions must not change identity on every streamed token.
  const latestRef = useRef({
    keys,
    activeKey,
    summaries,
    shown: null as SessionApi | null,
  });

  /**
   * The open sessions a new one joins, or null when none can open. Under
   * the cap, all of them. At the cap, a chat on screen that runs nothing is
   * replaced by the caller as before; one that runs keeps its stream, and
   * the oldest idle session makes way (CLE-567).
   */
  const keysBesideNewSession = useCallback((): string[] | null => {
    const { keys, activeKey, summaries, shown } = latestRef.current;
    if (keys.length < MAX_COPILOT_SESSIONS) return keys;
    if (!shown || !(shown.isStreaming || shown.awaitingConfirmation))
      return null;
    const spare = spareSession(keys, activeKey, summaries);
    if (!spare) return null;
    closersRef.current.get(spare)?.();
    return keys.filter((key) => key !== spare);
  }, []);

  const newSession = useCallback(() => {
    const { activeKey, shown } = latestRef.current;
    if (isBlankSession(shown, activeKey)) return true;
    const keys = keysBesideNewSession();
    if (!keys) return false;
    const key = crypto.randomUUID();
    dispatch(setAiChatSessions({ keys: [...keys, key], activeKey: key }));
    return true;
  }, [dispatch, keysBesideNewSession]);

  const switchSession = useCallback(
    (key: string) => {
      const { keys } = latestRef.current;
      if (keys.includes(key)) {
        dispatch(setAiChatSessions({ keys, activeKey: key }));
      }
    },
    [dispatch]
  );

  const closeSession = useCallback(
    (key: string) => {
      const { keys, activeKey } = latestRef.current;
      const index = keys.indexOf(key);
      if (keys.length < 2 || index < 0) return;
      closersRef.current.get(key)?.();
      const nextKeys = keys.filter((k) => k !== key);
      dispatch(
        setAiChatSessions({
          keys: nextKeys,
          activeKey:
            key === activeKey
              ? nextKeys[Math.min(index, nextKeys.length - 1)]
              : activeKey,
        })
      );
    },
    [dispatch]
  );

  const clearSessions = useCallback(() => {
    const { keys, activeKey, summaries } = latestRef.current;
    const kept = keys.filter((key) => {
      const status = summaries[key]?.status;
      return status === 'running' || status === 'needs_input';
    });
    for (const key of keys) {
      if (!kept.includes(key)) closersRef.current.get(key)?.();
    }
    // Always leave one session open; the primary was just cleared (or is
    // already idle and hidden), so it serves as the blank one.
    const nextKeys = kept.length > 0 ? kept : [PRIMARY_SESSION_KEY];
    dispatch(
      setAiChatSessions({
        keys: nextKeys,
        activeKey: nextKeys.includes(activeKey) ? activeKey : nextKeys[0],
      })
    );
  }, [dispatch]);

  const openConversation = useCallback(
    (conversationId: string) => {
      const { keys, activeKey, summaries, shown } = latestRef.current;
      const openKey = keys.find(
        (key) => summaries[key]?.conversationId === conversationId
      );
      if (openKey) {
        switchSession(openKey);
        return;
      }
      const beside = !isBlankSession(shown, activeKey)
        ? keysBesideNewSession()
        : null;
      if (!beside) {
        void shown?.loadConversation(conversationId);
        return;
      }
      // The new host starts with this conversationId and no messages, so
      // useAgentChat's auto-load fetches it on mount.
      const key = crypto.randomUUID();
      dispatch(
        setAiChatSessionState({
          key,
          chatState: {
            messages: [],
            conversationId,
            awaitingConfirmation: false,
            pendingConfirmation: null,
          },
        })
      );
      dispatch(setAiChatSessions({ keys: [...beside, key], activeKey: key }));
    },
    [dispatch, keysBesideNewSession, switchSession]
  );

  // A chat another surface opens in the dock (`openAiChatConversation`): the
  // Find search bar's question, answered in its own chat, opens there as a
  // chat picked from the history does.
  const conversationToOpen = useAppSelector(selectAiChatOpenConversationId);
  useEffect(() => {
    if (!enabled || !conversationToOpen) return;
    dispatch(openAiChatConversation(undefined));
    openConversation(conversationToOpen);
  }, [conversationToOpen, dispatch, enabled, openConversation]);

  // ThreadList "New chat": a fresh session, else a clear in place.
  const startNewChat = useCallback(() => {
    if (!newSession()) latestRef.current.shown?.clearChat();
    announceFreshChat();
  }, [newSession]);

  const onSummary = useCallback(
    (
      key: string,
      { running, needsInput, hasResponse, isActive, ...rest }: SessionReport
    ) => {
      setSummaries((prev) => {
        const before = prev[key]?.status;
        // A response that finishes in the background stays unread until the
        // session is viewed (becomes active).
        const unread =
          !isActive &&
          (before === 'unread' || (before === 'running' && !running));
        const status: CopilotSessionStatus = running
          ? 'running'
          : needsInput
            ? 'needs_input'
            : unread
              ? 'unread'
              : hasResponse
                ? 'done'
                : 'idle';
        return { ...prev, [key]: { key, kind: 'clu', ...rest, status } };
      });
    },
    []
  );

  const registerCloser = useCallback((key: string, close: () => void) => {
    closersRef.current.set(key, close);
    return () => {
      closersRef.current.delete(key);
    };
  }, []);

  const primaryIsActive = !agentAttachment && activeKey === PRIMARY_SESSION_KEY;
  const primaryRuntime = useAgentChatRuntime(context, {
    enabled,
    lists: allLists,
    onSelectList,
    isActive: primaryIsActive,
    organizationId,
    onNewThread: startNewChat,
    onOpenConversation: openConversation,
  });
  const primary = useSessionWiring(PRIMARY_SESSION_KEY, primaryRuntime, {
    isActive: primaryIsActive,
    enabled,
    newSession,
    onSummary,
    registerCloser,
  });

  // Only the ACTIVE session's runtime reaches AssistantRuntimeProvider, so a
  // background session's tool surfaces (auto-navigation, confirmation cards,
  // progress registration) never render - they fire when it is switched to.
  // An extra session's host reports its runtime from a layout effect, i.e.
  // one pre-paint pass after it becomes active. For that pass keep the last
  // displayed session (its surfaces are already mounted, so nothing new
  // fires); on a fresh mount there is none, so show an empty thread.
  const idleRuntime = useExternalStoreRuntime(IDLE_ADAPTER);
  const displayKey = agentAttachment?.key ?? activeKey;
  const idleSession = useMemo<SessionApi>(
    () => ({
      ...primary,
      key: displayKey,
      runtime: idleRuntime,
      messages: NO_MESSAGES,
      isStreaming: false,
      conversationId: null,
      awaitingConfirmation: false,
      pendingConfirmation: null,
      contextMeter: null,
      progress: null,
      modelPick: 'auto',
      isLoadingConversation: false,
      error: null,
      errorCode: null,
      failedTextTurn: null,
      messageQueue: null,
      sendMessage: async () => ({ ok: false, aborted: true }),
      acceptedPlayReviews: [],
      retryAcceptedPlayReview: async () => {},
      confirmAction: async () => undefined,
      rejectAction: async () => undefined,
    }),
    [displayKey, idleRuntime, primary]
  );
  const lastShownRef = useRef<SessionApi | null>(null);
  const shown = primaryIsActive
    ? primary
    : reported?.key === displayKey
      ? reported
      : !agentAttachment &&
          lastShownRef.current &&
          keys.includes(lastShownRef.current.key)
        ? lastShownRef.current
        : idleSession;
  lastShownRef.current = shown;
  latestRef.current = { keys, activeKey, summaries, shown };

  // The agents' chats whose turn runs, by host: an agent's chat goes on
  // after its workspace is left, and the sidebar still marks it running.
  const [agentRunning, setAgentRunning] = useState<Record<string, string>>({});
  const onAgentSummary = useCallback(
    (key: string, report: SessionReport) => {
      reportConversation(key, report.conversationId);
      const running = report.running ? report.conversationId : null;
      setAgentRunning((prev) => {
        if ((prev[key] ?? null) === running) return prev;
        const next = { ...prev };
        if (running) next[key] = running;
        else delete next[key];
        return next;
      });
    },
    [reportConversation]
  );
  const running: string[] = [];
  if (primary.isStreaming && primary.conversationId)
    running.push(primary.conversationId);
  for (const key of keys) {
    const session = summaries[key];
    if (session?.status === 'running' && session.conversationId)
      running.push(session.conversationId);
  }
  for (const host of agentHosts.hosts) {
    if (agentRunning[host.key]) running.push(agentRunning[host.key]);
  }
  if (shown.isStreaming && shown.conversationId)
    running.push(shown.conversationId);
  const runningChatIds = useStableIds(running);
  const startAgentChat = useCallback(() => {
    if (newAgentChat()) announceFreshChat();
  }, [newAgentChat]);
  const loadAgentConversation = useCallback(
    async (id: string) => {
      // Explicit refresh (for example, the saved line after accepting a
      // proposal) remains available on the selected idle conversation.
      // Selecting another chat, or reselecting a running one, never reloads it.
      if (
        shown.key === agentAttachment?.key &&
        shown.conversationId === id &&
        !shown.isStreaming
      )
        await shown.loadConversation(id);
      else openAgentConversation(id);
    },
    [agentAttachment?.key, openAgentConversation, shown]
  );

  const deleteConversation = useCallback(
    async (id: string) => {
      const hostKeys = agentHosts.hosts
        .filter((host) => host.conversationId === id)
        .map((host) => host.key);
      const deleted = await shown.deleteConversation(id);
      if (deleted) forgetAgentHosts(hostKeys);
      return deleted;
    },
    [agentHosts.hosts, forgetAgentHosts, shown]
  );

  const canOpenSession = keys.length < MAX_COPILOT_SESSIONS;
  const sessions = useMemo(
    () =>
      keys.map(
        (key): CopilotSession =>
          summaries[key] ?? {
            key,
            kind: 'clu',
            title: 'New chat',
            conversationId: null,
            status: 'idle',
            error: false,
          }
      ),
    [keys, summaries]
  );

  // Destination list for card-driven actions (Reveal / Add to list) when the
  // user hasn't picked one: active-page list -> protected "Extension Leads".
  const defaultListId = useMemo(() => {
    if (aiChatContext?.type === 'lead_list' && aiChatContext.lead_list_id) {
      return aiChatContext.lead_list_id;
    }
    return allLists?.find((l) => l.is_protected)?.id ?? null;
  }, [aiChatContext, allLists]);

  const { listMentionItems } = shown;
  // Add to list never offers Extension Leads; Reveal still saves there
  // through defaultListId above.
  const toolUiLists = useMemo(() => {
    const locked = new Set(
      (allLists ?? []).filter((l) => isExtensionLeads(l)).map((l) => l.id)
    );
    return listMentionItems
      .filter((l) => l.id !== 'all' && !locked.has(l.id))
      .map((l) => ({ id: l.id, name: l.label }));
  }, [listMentionItems, allLists]);

  // Question cards re-render whenever this changes. It depends on the four
  // values a card reads, so a streamed chunk leaves it alone.
  const { conversationId, isStreaming, isLoadingConversation, sendMessage } =
    shown;
  const questionCardInput = useMemo<QuestionCardInput>(
    () => ({
      conversationId,
      isStreaming,
      isLoadingConversation,
      send: (message, userInput, expectedConversationId) =>
        sendMessage(message, { userInput, expectedConversationId }),
    }),
    [conversationId, isStreaming, isLoadingConversation, sendMessage]
  );

  const contextValue = useMemo<CopilotRuntimeContextValue>(
    () => ({
      attachAgentChat: agentHosts.attach,
      enqueueAcceptedPlayReview: agentHosts.enqueueAcceptedReview,
      acceptedPlayReviews: shown.acceptedPlayReviews,
      savedPlayReviewRequests: agentHosts.hosts.flatMap((host) => host.reviews),
      interruptedPlayReviews: agentHosts.interruptedPlays,
      markPlayReviewInterrupted: agentHosts.markPlayReviewInterrupted,
      retryAcceptedPlayReview: shown.retryAcceptedPlayReview,
      agentSession: agentAttachment
        ? {
            agentId: agentAttachment.agentId,
            requestedChatId: agentAttachment.requestedChatId,
            ready: shown.key === agentAttachment.key && shown !== idleSession,
          }
        : null,
      messages: shown.messages,
      isStreaming: shown.isStreaming,
      conversationId: shown.conversationId,
      runningChatIds,
      awaitingConfirmation: shown.awaitingConfirmation,
      conversations: shown.conversations,
      historyFailed: shown.historyFailed,
      historyScope: shown.historyScope,
      isLoadingHistory: shown.isLoadingHistory,
      localChats: shown.localChats,
      settleAgentChats: shown.settleAgentChats,
      clearChat: agentAttachment ? startAgentChat : shown.clearChat,
      fetchConversations: shown.fetchConversations,
      deleteConversation,
      sendMessage: shown.sendMessage,
      failedTextTurn: shown.failedTextTurn,
      retryFailedTextTurn: shown.retryFailedTextTurn,
      isLoadingConversation: shown.isLoadingConversation,
      loadConversation: agentAttachment
        ? loadAgentConversation
        : shown.loadConversation,
      snapshotChat: shown.snapshotChat,
      replaceChat: shown.replaceChat,
      error: shown.error,
      errorCode: shown.errorCode,
      contextMeter: shown.contextMeter,
      progress: shown.progress,
      modelPick: shown.modelPick,
      setModelPick: shown.setModelPick,
      listMentionAdapter: shown.listMentionAdapter,
      listMentionFormatter: shown.listMentionFormatter,
      listMentionItems: shown.listMentionItems,
      lists: allLists,
      sessions: agentAttachment ? [] : sessions,
      activeSessionKey: displayKey,
      canOpenSession: agentAttachment ? true : canOpenSession,
      newSession: agentAttachment ? agentHosts.newChat : newSession,
      startNewChat: agentAttachment ? startAgentChat : startNewChat,
      switchSession,
      closeSession,
      clearSessions,
      openConversation: agentAttachment
        ? agentHosts.openConversation
        : openConversation,
    }),
    [
      shown,
      runningChatIds,
      allLists,
      sessions,
      canOpenSession,
      newSession,
      startNewChat,
      switchSession,
      closeSession,
      clearSessions,
      openConversation,
      agentAttachment,
      agentHosts.attach,
      agentHosts.enqueueAcceptedReview,
      agentHosts.hosts,
      agentHosts.interruptedPlays,
      agentHosts.markPlayReviewInterrupted,
      agentHosts.newChat,
      agentHosts.openConversation,
      displayKey,
      idleSession,
      startAgentChat,
      loadAgentConversation,
      deleteConversation,
    ]
  );

  return (
    <>
      {keys
        .filter((key) => key !== PRIMARY_SESSION_KEY)
        .map((key) => (
          <CopilotSessionHost
            key={key}
            sessionKey={key}
            isActive={!agentAttachment && key === activeKey}
            enabled={enabled}
            context={context}
            organizationId={organizationId}
            lists={allLists}
            onSelectList={onSelectList}
            newSession={newSession}
            onNewThread={startNewChat}
            onOpenConversation={openConversation}
            onSummary={onSummary}
            registerCloser={registerCloser}
            onActive={setReported}
          />
        ))}
      {agentHosts.hosts.map((host) => (
        <CopilotSessionHost
          key={host.key}
          sessionKey={host.key}
          isActive={host.key === agentAttachment?.key}
          enabled={agentsEnabled}
          organizationId={organizationId}
          context={host.context}
          acceptedPlayReviews={host.reviews}
          lists={allLists}
          onSelectList={onSelectList}
          newSession={agentHosts.newChat}
          onNewThread={startAgentChat}
          onOpenConversation={agentHosts.openConversation}
          onSummary={onAgentSummary}
          registerCloser={registerCloser}
          onActive={setReported}
        />
      ))}
      <AssistantRuntimeProvider runtime={shown.runtime}>
        <CopilotToolUIs
          onConfirm={shown.confirmAction}
          onReject={shown.rejectAction}
          pendingConfirmationId={shown.pendingConfirmation?.id ?? null}
          pendingConfirmationActionToken={
            shown.pendingConfirmation?.action_token ?? null
          }
          lists={toolUiLists}
          defaultListId={defaultListId}
        >
          <CopilotRuntimeContext.Provider value={contextValue}>
            <MessageQueueContext.Provider value={shown.messageQueue}>
              <QuestionCardInputContext.Provider value={questionCardInput}>
                {children}
              </QuestionCardInputContext.Provider>
            </MessageQueueContext.Provider>
          </CopilotRuntimeContext.Provider>
        </CopilotToolUIs>
      </AssistantRuntimeProvider>
    </>
  );
}

type SessionWiring = {
  isActive: boolean;
  enabled: boolean;
  newSession: () => boolean;
  onSummary?: (key: string, report: SessionReport) => void;
  registerCloser: (key: string, close: () => void) => () => void;
};

/**
 * Per-session wiring shared by the primary session (owned by the provider)
 * and every extra session host. Effects that act on the user's screen - the
 * seeded prompt and the history refresh - run for the ACTIVE session only.
 */
function useSessionWiring(
  key: string,
  api: RuntimeApi,
  { isActive, enabled, newSession, onSummary, registerCloser }: SessionWiring
): SessionApi {
  const dispatch = useAppDispatch();
  const aiChatInitialMessage = useAppSelector(selectAiChatInitialMessage);
  const aiChatInitialModelPick = useAppSelector(selectAiChatInitialModelPick);
  const aiChatPendingSend = useAppSelector(selectAiChatPendingSend);
  const {
    runtime,
    acceptedPlayReviews,
    retryAcceptedPlayReview,
    messages,
    isStreaming,
    error,
    errorCode,
    conversationId,
    awaitingConfirmation,
    pendingConfirmation,
    contextMeter,
    progress,
    modelPick,
    setModelPick,
    conversations: untitledConversations,
    historyFailed,
    historyScope,
    isLoadingHistory,
    localChats,
    settleAgentChats,
    isLoadingConversation,
    sendMessage,
    failedTextTurn,
    retryFailedTextTurn,
    confirmAction,
    rejectAction,
    clearChat,
    fetchConversations,
    loadConversation,
    deleteConversation,
    snapshotChat,
    replaceChat,
    messageQueue,
    listMentionAdapter,
    listMentionFormatter,
    listMentionItems,
  } = api;

  useEffect(
    () => registerCloser(key, clearChat),
    [clearChat, key, registerCloser]
  );

  // Refresh conversation history when idle (was AiSidebar 187-191).
  useEffect(() => {
    if (!enabled || !isActive) return;
    if (isStreaming) return;
    void fetchConversations();
  }, [enabled, isActive, isStreaming, conversationId, fetchConversations]);

  // External-query handoff: a Find-People submission sets
  // `aiChatInitialMessage` + opens a surface; send it into a fresh thread
  // (was AiSidebar 178/184 refs + 193-221 effect). Re-gated on `enabled` so
  // the handoff also works when landing straight on `/clu`. Only the
  // ACTIVE session consumes the seed.
  useEffect(() => {
    if (!enabled || !isActive) return;
    if (!aiChatInitialMessage) return;

    // Seeded prompt → fresh thread. With multiple sessions, open a new one
    // (the new active session consumes the seed; nothing is cleared or
    // aborted). Otherwise, if a prior thread is still loaded, clear it first;
    // the effect re-fires once messages is [] and sends below.
    // NEVER abort an in-flight stream: clearChat() cancels the SSE reader, so
    // when a stream is running (e.g. a quick-start seed fired mid-stream) we
    // wait — the effect re-fires when isStreaming flips false and clears then.
    if (messages.length > 0) {
      if (newSession()) return;
      if (isStreaming) return;
      clearChat();
      return;
    }

    // Consume the one-shot seed as we send it. This is the SOLE re-fire guard:
    // once consumed, the `!aiChatInitialMessage` check above short-circuits, so
    // the same seed can't re-send even if `enabled` toggles false→true. (A
    // same-text dedupe gate used to live here, but once the home surface
    // started clearing the thread before seeding, that gate swallowed
    // deliberate re-submits of an identical prompt — e.g. re-clicking the
    // Enrich quick-start — and dropped the user into an empty conversation.)
    // clu_model_v1 router: the model the launcher chose for this chat. The
    // fresh chat takes it now, after any clearing above, and the seed goes
    // out on it as the chat's first turn. Always applied, Auto included: a
    // blank chat whose model was changed (Grok picked in /clu, then home) is
    // not cleared above, so it would send on that stale pick. A seed that
    // chose none (another surface's, or a launcher left on the default) puts
    // the chat back on the member's default, which the server applies.
    void setModelPick(
      aiChatInitialModelPick === undefined
        ? null
        : cluModelPickOf(aiChatInitialModelPick)
    );
    void sendMessage(aiChatInitialMessage);
    dispatch(setAiChatInitialMessage(undefined));
  }, [
    aiChatInitialMessage,
    aiChatInitialModelPick,
    dispatch,
    enabled,
    isActive,
    isStreaming,
    messages.length,
    newSession,
    sendMessage,
    setModelPick,
    clearChat,
  ]);

  // A message another surface sends as the user into the CURRENT conversation
  // (the agent workspace's composer, a starter card). Never a fresh thread:
  // building an agent stays in that chat. Hand it to the selected runtime's
  // own queue immediately, so leaving while it streams cannot give another
  // Agent the pending text. Only the ACTIVE session consumes the handoff.
  useEffect(() => {
    if (!enabled || !isActive) return;
    if (!aiChatPendingSend) return;
    const text = aiChatPendingSend;
    dispatch(sendAiChatMessage(undefined));
    runtime.thread.append({ role: 'user', content: [{ type: 'text', text }] });
  }, [aiChatPendingSend, dispatch, enabled, isActive, runtime]);

  // The history with the titles users gave their chats, from the moment
  // they give them (see chat-titles.store).
  const titles = useChatTitles();
  const conversations = useMemo(
    () => withChatTitles(untitledConversations, titles),
    [untitledConversations, titles]
  );

  const title = getConversationLabel({
    conversationId,
    conversations,
    messages,
  });
  const hasResponse = messages.some((m) => m.role === 'assistant');
  useEffect(() => {
    onSummary?.(key, {
      title,
      conversationId,
      error: error !== null,
      running: isStreaming,
      needsInput: awaitingConfirmation,
      hasResponse,
      isActive,
    });
  }, [
    awaitingConfirmation,
    conversationId,
    error,
    hasResponse,
    isActive,
    isStreaming,
    key,
    onSummary,
    title,
  ]);

  return useMemo(
    () => ({
      key,
      runtime,
      acceptedPlayReviews,
      retryAcceptedPlayReview,
      messages,
      isStreaming,
      conversationId,
      awaitingConfirmation,
      pendingConfirmation,
      contextMeter,
      progress,
      modelPick,
      setModelPick,
      conversations,
      historyFailed,
      historyScope,
      isLoadingHistory,
      localChats,
      settleAgentChats,
      isLoadingConversation,
      error,
      errorCode,
      sendMessage,
      failedTextTurn,
      retryFailedTextTurn,
      confirmAction,
      rejectAction,
      clearChat,
      fetchConversations,
      loadConversation,
      deleteConversation,
      snapshotChat,
      replaceChat,
      messageQueue,
      listMentionAdapter,
      listMentionFormatter,
      listMentionItems,
    }),
    [
      key,
      runtime,
      acceptedPlayReviews,
      retryAcceptedPlayReview,
      messages,
      isStreaming,
      conversationId,
      awaitingConfirmation,
      pendingConfirmation,
      contextMeter,
      progress,
      modelPick,
      setModelPick,
      conversations,
      historyFailed,
      historyScope,
      isLoadingHistory,
      localChats,
      settleAgentChats,
      isLoadingConversation,
      error,
      errorCode,
      sendMessage,
      failedTextTurn,
      retryFailedTextTurn,
      confirmAction,
      rejectAction,
      clearChat,
      fetchConversations,
      loadConversation,
      deleteConversation,
      snapshotChat,
      replaceChat,
      messageQueue,
      listMentionAdapter,
      listMentionFormatter,
      listMentionItems,
    ]
  );
}

/**
 * One extra session: owns its own runtime and SSE
 * stream, and stays mounted while the session is open so a background stream
 * keeps running. Renders nothing; while active it hands its runtime to the
 * provider.
 */
const CopilotSessionHost = memo(function CopilotSessionHost({
  sessionKey,
  context,
  organizationId,
  acceptedPlayReviews,
  lists,
  onSelectList,
  onNewThread,
  onOpenConversation,
  onActive,
  ...wiring
}: SessionWiring & {
  sessionKey: string;
  context: AgentContext;
  organizationId?: string;
  acceptedPlayReviews?: readonly AcceptedPlayReviewRequest[];
  lists: LeadList[] | undefined;
  onSelectList: (list: LeadList | null) => void;
  onNewThread: () => void;
  onOpenConversation: (conversationId: string) => void;
  onActive: (api: SessionApi) => void;
}) {
  const runtime = useAgentChatRuntime(context, {
    enabled: wiring.enabled,
    lists,
    onSelectList,
    sessionKey,
    organizationId,
    acceptedPlayReviews,
    isActive: wiring.isActive,
    onNewThread,
    onOpenConversation,
  });
  const api = useSessionWiring(sessionKey, runtime, wiring);
  const { isActive } = wiring;
  useLayoutEffect(() => {
    if (isActive) onActive(api);
  }, [api, isActive, onActive]);
  return null;
});
